import { Box, Card, FormControl, InputLabel, Select, MenuItem, Tabs, Tab } from "@mui/material";
import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import AppService from "../../../../Configurations/Appservice";
import pxToRem from "../../../../Themes/Functions/pxToRem";
import BalanceCard from "./panels/BalanceCard";
import { useDispatch, useSelector } from "react-redux";
import { updateScreenTitle } from "../../../../LayoutContainers/AppLayout/redux/AppSlice";
import TicketMapping from "./panels/TicketMapping";
import PendingApprovalsSimplified from "./panels/PendingApprovalsSimplified";
import useWallet from "../../../../hooks/useWallet";
import moment from "moment";
import uuid from "react-uuid";
import { noScrollBarStyle } from "../../../../Configurations/AppStyle";
import DateRangeFilter from "./components/DateRangeFilter";


const MyWallet = (props) => {
  const [isPartnerUser] = useState(() => {
    try {
      const authInfo = JSON.parse(localStorage.getItem('authInfo') || '{}');
      const userType = authInfo?.principalInfo?.userType;
      return userType === 'PARTNER_ORG' || userType === 'CHANNEL_PARTNER_ORG';
    } catch (e) { return false; }
  });
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [ticketMappings, setTicketMappings] = useState([]);
  const [isRootAdmin, setIsRootAdmin] = useState(false);
  const [partners, setPartners] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [currentTab, setCurrentTab] = useState(0);
  const [dateFilter, setDateFilter] = useState({ startDate: "", endDate: "" });
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);

  // Use wallet hook for all wallet-related data
  const {
    balance,
    transactions,
    unpaidTickets,
    loading,
    error,
    fetchBalance,
    fetchTransactions,
    fetchUnpaidTickets
  } = useWallet();

  // DEBUG: Log hook values immediately on first render
  console.log("\n╔════════════════════════════════════════╗");
  console.log("║   MyWallet Component - RENDER START    ║");
  console.log("╚════════════════════════════════════════╝");
  console.log("[MyWallet-RENDER] unpaidTickets:", unpaidTickets?.length || 0, "items");
  console.log("[MyWallet-RENDER] balance:", balance);
  console.log("[MyWallet-RENDER] loading:", loading);
  console.log("[MyWallet-RENDER] error:", error);

  useEffect(() => {
    console.log("\n[MyWallet] ========== COMPONENT MOUNTED ==========");
    dispatch(updateScreenTitle("My Wallet"));
    return () => {
      console.log("[MyWallet] Component will unmount");
      dispatch(updateScreenTitle(""));
    };
  }, [dispatch]);

  useEffect(() => {
    AppService.get("v1/wallet/paymentTypes")
      .then((response) => {
        setPaymentTypes(response);
      })
      .catch((error) => {})
      .finally(() => {});
  }, []);

  // Fetch wallet balance on component mount
  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]);

  // Detect root admin and load partner list
  useEffect(() => {
    console.log("[MyWallet] Starting auth detection...");
    try {
      const authInfo = JSON.parse(localStorage.getItem('authInfo') || '{}');
      console.log("[MyWallet] Auth info retrieved:", authInfo?.principalInfo?.email);
      const roles = authInfo?.principalInfo?.roles || [];
      console.log("[MyWallet] Roles found:", roles.length, "roles");
      const isRoot = Array.isArray(roles) && roles.some((r) => {
        const val = typeof r === 'string' ? r : (r?.name || r?.code || r?.authority || '');
        const s = String(val);
        return /(root\s*admin|role_root_admin|root_admin)/i.test(s) || (s.toLowerCase().includes('root') && s.toLowerCase().includes('admin'));
      });
      console.log("[MyWallet] isRoot admin detected:", isRoot);
      setIsRootAdmin(isRoot);
      if (isRoot) {
        console.log("[MyWallet] Root admin confirmed, fetching partners...");
        AppService.get("v1/organisation/keyValues?type=PARTNER")
          .then((resp) => {
            const list = Array.isArray(resp) ? resp : resp?.data || [];
            console.log("[MyWallet] Partners loaded:", list.length);
            setPartners(list);
            // Default to first partner if none selected yet
            if ((!selectedOrgId || selectedOrgId === "") && list.length > 0) {
              const first = list[0];
              const firstId = (first.id ?? first.value) || first.key || "";
              if (firstId) {
                setSelectedOrgId(firstId);
                console.log("[MyWallet] First partner auto-selected:", firstId);
              }
            }
          })
          .catch(() => {
            console.error("[MyWallet] Failed to load partners");
            setPartners([]);
          });
      } else {
        console.log("[MyWallet] Not root admin, skipping partner load");
      }
    } catch (e) {
      console.error("[MyWallet] Auth detection error:", e);
      setIsRootAdmin(false);
    }
  }, []);

  // Fetch transactions when Transaction History tab is selected
  useEffect(() => {
    if (currentTab === 1) {
      console.log("[MyWallet] Transaction History tab selected, loading wallet transactions...");
      fetchTransactions().catch(err => {
        console.error("[MyWallet] Failed to load wallet transactions:", err);
      });
    }
  }, [currentTab, fetchTransactions]);

  // Fetch unpaid tickets
  useEffect(() => {
    console.log("[MyWallet] ===== UNPAID TICKETS EFFECT TRIGGERED =====");
    console.log("[MyWallet] isRootAdmin:", isRootAdmin, "| selectedOrgId:", selectedOrgId);
    console.log("[MyWallet] Condition check - isRootAdmin && selectedOrgId:", isRootAdmin && selectedOrgId);
    console.log("[MyWallet] Condition check - !isRootAdmin:", !isRootAdmin);

    if (isRootAdmin && selectedOrgId) {
      console.log("[MyWallet] ✓ CONDITION 1: Fetching unpaid tickets for partner:", selectedOrgId);
      fetchUnpaidTickets(selectedOrgId);
    } else if (!isRootAdmin) {
      console.log("[MyWallet] ✓ CONDITION 2: Fetching unpaid tickets for current user (no param)");
      fetchUnpaidTickets();
    } else {
      console.log("[MyWallet] ✗ CONDITION 3: Waiting for org selection (isRootAdmin=true but no selectedOrgId)");
    }
    console.log("[MyWallet] ===== END UNPAID TICKETS EFFECT =====");
  }, [isRootAdmin, selectedOrgId, fetchUnpaidTickets]);

  // Log when unpaidTickets change
  useEffect(() => {
    console.log("[MyWallet] unpaidTickets updated - count:", (unpaidTickets || []).length);
    console.log("[MyWallet] unpaidTickets data:", unpaidTickets);
  }, [unpaidTickets]);

  // Fetch transaction history
  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // Handle wallet balance update after ticket processing
  const handleBalanceUpdate = (newBalance) => {
    // Balance is already managed by useWallet hook
    // Just fetch fresh data
    fetchBalance();
  };

  // Filter transactions by date range
  const filteredTransactions = (transactions || []).filter(transaction => {
    if (!dateFilter.startDate && !dateFilter.endDate) return true;

    const transactionDate = moment(transaction.paymentDate || transaction.createdDate);
    const start = dateFilter.startDate ? moment(dateFilter.startDate) : null;
    const end = dateFilter.endDate ? moment(dateFilter.endDate).endOf('day') : null;

    if (start && end) {
      return transactionDate.isBetween(start, end, null, '[]');
    } else if (start) {
      return transactionDate.isSameOrAfter(start);
    } else if (end) {
      return transactionDate.isSameOrBefore(end);
    }
    return true;
  });

  // DEBUG: Log filtered transactions to verify field availability
  console.log("\n[MyWallet] ========== FILTERED TRANSACTIONS DEBUG ==========");
  console.log("[MyWallet] Total transactions:", transactions?.length || 0);
  console.log("[MyWallet] Filtered transactions:", filteredTransactions.length);
  if (filteredTransactions.length > 0) {
    console.log("[MyWallet] Sample transaction (first):");
    const sample = filteredTransactions[0];
    console.log("  - ID:", sample.id);
    console.log("  - Transaction Number:", sample.transactionNumber);
    console.log("  - Amount:", sample.amount);
    console.log("  - TYPE:", sample.type, "(typeof:", typeof sample.type, ")");
    console.log("  - STATUS:", sample.status, "(typeof:", typeof sample.status, ")");
    console.log("  - Description:", sample.description);
    console.log("  - Created Date:", sample.createdDate);
    console.log("  - Full object:", sample);
  }
  console.log("[MyWallet] ===================================================\n");

  // Redirect partner users to Payment Dashboard
  if (isPartnerUser) {
    return <Navigate to="/wallet/payment-dashboard" replace />;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: `calc(100vh - ${pxToRem(55)})`,
        width: "100%",
      }}
    >
      {/* Wallet Balance at the top */}
      <Box sx={{ mb: 1.25, width: "100%" }}>
        <BalanceCard
          screenData={{ balance }}
          paymentTypes={paymentTypes}
          unpaidTickets={unpaidTickets || []}
          onTopupSuccess={(latestBalance) => {
            // Refresh balance after successful topup
            fetchBalance();
          }}
        />
      </Box>

      {/* Pending Approvals Summary Card */}
      <Box sx={{ mb: 1.25, width: "100%" }}>
        <PendingApprovalsSimplified />
      </Box>

      {/* Ticket Mapping Section */}
      <Box sx={{ width: '100%', flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Root Admin: Partner Organization Filter */}
        {isRootAdmin && (
          <Card sx={{ p: 1.25, mb: 1 }}>
            <FormControl size="small" sx={{ minWidth: 260 }}>
              <InputLabel id="partner-org-select-label">Partner Organization</InputLabel>
              <Select
                labelId="partner-org-select-label"
                label="Partner Organization"
                value={selectedOrgId}
                onChange={(e) => setSelectedOrgId(e.target.value)}
              >
                <MenuItem value="" disabled>Select Partner</MenuItem>
                {(partners || []).map((p) => (
                  <MenuItem key={(p.id ?? p.value) || p.key} value={(p.id ?? p.value) || p.key}>
                    {p.name ?? p.label ?? p.text ?? p.value ?? p.id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Card>
        )}

        <Box sx={{ flexGrow: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          {console.log("[MyWallet-RENDER] About to render Card with Tabs | currentTab:", currentTab, "| unpaidTickets:", unpaidTickets?.length || 0)}
          <Card sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Tab Navigation */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              {console.log("[MyWallet-RENDER] Rendering Tabs component | currentTab value:", currentTab)}
              <Tabs
                value={currentTab}
                onChange={(e, newValue) => {
                  console.log("\n╔════════════════════════════════════════╗");
                  console.log("║          TAB CLICKED - HANDLER FIRED   ║");
                  console.log("╚════════════════════════════════════════╝");
                  console.log("[Payments-TAB] New tab value:", newValue);
                  console.log("[Payments-TAB] Current unpaidTickets count:", (unpaidTickets || []).length);
                  console.log("[Payments-TAB] Current loading state:", loading);
                  console.log("[Payments-TAB] Current error state:", error);
                  console.log("[Payments-TAB] isRootAdmin:", isRootAdmin);
                  console.log("[Payments-TAB] selectedOrgId:", selectedOrgId);
                  console.log("[Payments-TAB] CALLING setCurrentTab with value:", newValue);
                  setCurrentTab(newValue);
                  console.log("[Payments-TAB] setCurrentTab called successfully");
                  console.log("╚════════════════════════════════════════╝\n");
                }}
                sx={{ px: 2 }}
              >
                <Tab label="Select Ticket & Payment" />
                <Tab label="Transaction History" />
              </Tabs>
            </Box>

            {/* Tab Content */}
            <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
              {/* Tab 0: Ticket Selection & Payment */}
              {currentTab === 0 && (
                <Box sx={{ p: 2 }}>
                  <TicketMapping
                    mappings={unpaidTickets || []}
                    walletBalance={balance || 0}
                    onBalanceUpdate={handleBalanceUpdate}
                    selectedOrgId={isRootAdmin ? selectedOrgId : ""}
                  />
                </Box>
              )}

              {/* Tab 1: Transaction History */}
              {currentTab === 1 && (
                <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                  {/* Date Range Filter */}
                  <Box sx={{ mb: 2 }}>
                    <DateRangeFilter onFilterChange={setDateFilter} />
                  </Box>

                  {/* Transaction List with Sections */}
                  <Box sx={{ flexGrow: 1, overflow: 'auto', ...noScrollBarStyle }}>
                    {filteredTransactions.length > 0 ? (
                      <>
                        {/* Wallet Top-ups Section (CREDIT transactions) */}
                        {filteredTransactions.filter(t => t.type === 'CREDIT').length > 0 && (
                          <Box sx={{ mb: 3 }}>
                            <Box sx={{ fontSize: 16, fontWeight: 600, color: '#00897B', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#00897B' }} />
                              Wallet Top-ups (Money IN)
                            </Box>
                            {filteredTransactions.filter(t => t.type === 'CREDIT').map((transaction, index) => {
                              // DEBUG: Log each transaction being rendered
                              console.log(`[MyWallet-RENDER] CREDIT Transaction #${index}:`, {
                                id: transaction.id,
                                transactionNumber: transaction.transactionNumber,
                                type: transaction.type,
                                status: transaction.status,
                                amount: transaction.amount,
                                description: transaction.description
                              });

                              return (
                              <Box
                                key={uuid()}
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  p: 1.5,
                                  mb: 1,
                                  border: 1,
                                  borderColor: '#00897B',
                                  borderRadius: 1,
                                  backgroundColor: '#E0F2F1',
                                  '&:hover': { backgroundColor: '#B2DFDB' }
                                }}
                              >
                                <Box sx={{ flex: 1 }}>
                                  <Box sx={{ fontSize: 14, fontWeight: 500, color: 'text.primary' }}>
                                    {transaction.description || transaction.transactionNumber || "Wallet Top-up"}
                                  </Box>
                                  <Box sx={{ fontSize: 12, color: 'text.secondary' }}>
                                    {moment(transaction.createdDate).format("MMM DD, YYYY HH:mm")}
                                  </Box>
                                </Box>
                                <Box sx={{ textAlign: 'right' }}>
                                  <Box sx={{ fontSize: 14, fontWeight: 600, color: '#00897B' }}>
                                    + {transaction.amount ? Number(transaction.amount).toFixed(2) : '0.00'} AED
                                  </Box>
                                  <Box sx={{ fontSize: 12, color: 'text.secondary' }}>
                                    {console.log(`[MyWallet-RENDER] Displaying status for transaction ${transaction.transactionNumber}:`, transaction.status || "COMPLETED")}
                                    {transaction.status || "COMPLETED"}
                                  </Box>
                                </Box>
                              </Box>
                            )}
                            )}
                          </Box>
                        )}

                        {/* Ticket Payments Section (DEBIT transactions) */}
                        {filteredTransactions.filter(t => t.type === 'DEBIT').length > 0 && (
                          <Box>
                            <Box sx={{ fontSize: 16, fontWeight: 600, color: '#D32F2F', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#D32F2F' }} />
                              Ticket Payments (Money OUT)
                            </Box>
                            {filteredTransactions.filter(t => t.type === 'DEBIT').map((transaction) => (
                              <Box
                                key={uuid()}
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  p: 1.5,
                                  mb: 1,
                                  border: 1,
                                  borderColor: '#D32F2F',
                                  borderRadius: 1,
                                  backgroundColor: '#FFEBEE',
                                  '&:hover': { backgroundColor: '#FFCDD2' }
                                }}
                              >
                                <Box sx={{ flex: 1 }}>
                                  <Box sx={{ fontSize: 14, fontWeight: 500, color: 'text.primary' }}>
                                    {transaction.description || transaction.transactionNumber || "Ticket Payment"}
                                  </Box>
                                  <Box sx={{ fontSize: 12, color: 'text.secondary' }}>
                                    {moment(transaction.createdDate).format("MMM DD, YYYY HH:mm")}
                                  </Box>
                                </Box>
                                <Box sx={{ textAlign: 'right' }}>
                                  <Box sx={{ fontSize: 14, fontWeight: 600, color: '#D32F2F' }}>
                                    - {transaction.amount ? Number(transaction.amount).toFixed(2) : '0.00'} AED
                                  </Box>
                                  <Box sx={{ fontSize: 12, color: 'text.secondary' }}>
                                    {transaction.status || "COMPLETED"}
                                  </Box>
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                        No transactions found for the selected date range
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default MyWallet;
